// Pricing calculator — matches the spec exactly.
// FOR PLAIN GOLD (diamond_weight == 0):
//   gold_value  = gold_weight × karat_rate
//   making      = gold_value × <karat-based%>
//   subtotal    = gold_value + making
//   gst         = subtotal × 3% (if ON)
//   final       = subtotal + gst
//
// FOR DIAMOND JEWELLERY (diamond_weight > 0):
//   making      = gold_value × 18.5%
// FOR BRACELETS (category contains "bracelet" — takes precedence over diamond rule):
//   making      = gold_value × 17%

export const DIAMOND_RATE_PER_CT = 60000;
export const GST_PERCENT = 3;

// Category-specific making-charge overrides (Feb 2026 spec).
// Bracelets override the diamond rule, so a diamond bracelet still pays 17%.
const isBracelet = (item) => {
  const cat = String(item?.category || "").toLowerCase();
  const name = String(item?.item_name || "").toLowerCase();
  return cat.includes("bracelet") || name.includes("bracelet");
};

export const getKaratRate = (rates, karat) => {
  if (!rates) return 0;
  switch ((karat || "22kt").toLowerCase()) {
    case "24kt": return rates.rate_24kt || 0;
    case "22kt": return rates.rate_22kt || 0;
    case "18kt": return rates.rate_18kt || 0;
    case "14kt": return rates.rate_14kt || 0;
    default: return rates.rate_22kt || 0;
  }
};

export const computePrice = (item, rates, gstOn) => {
  // Ringstudio items use fixed MRP (no gold weight tracked)
  if (item.collection === "ringstudio" && item.mrp && Number(item.mrp) > 0) {
    const sub = Number(item.mrp);
    const gst = gstOn ? sub * (GST_PERCENT / 100) : 0;
    return {
      isDiamond: Number(item.diamond_weight || 0) > 0,
      karatRate: 0,
      goldValue: 0,
      makingCharges: 0,
      makingPct: 0,
      diamondValue: 0,
      subtotal: Math.round(sub),
      gst: Math.round(gst),
      final: Math.round(sub + gst),
      mrpMode: true,
    };
  }

  const goldWeight = Number(item.gold_weight || 0);
  const diamondWeight = Number(item.diamond_weight || 0);
  const isDiamond = diamondWeight > 0;
  const karatRate = getKaratRate(rates, item.karat);

  const goldValue = goldWeight * karatRate;
  const makingPct = getMakingPct(item, isDiamond);
  const makingCharges = goldValue * makingPct;
  const diamondValue = isDiamond ? diamondWeight * DIAMOND_RATE_PER_CT : 0;
  const subtotal = goldValue + makingCharges + diamondValue;
  const gst = gstOn ? subtotal * (GST_PERCENT / 100) : 0;
  const final = subtotal + gst;

  return {
    isDiamond,
    karatRate,
    goldValue: round0(goldValue),
    makingCharges: round0(makingCharges),
    makingPct: makingPct * 100,
    diamondValue: round0(diamondValue),
    subtotal: round0(subtotal),
    gst: round0(gst),
    final: round0(final),
    mrpMode: false,
  };
};

// Making charges — Feb 2026 spec:
//   1. Bracelets   → 17 %  (overrides everything else)
//   2. Diamond     → 18.5 %
//   3. Plain gold  → karat-based: 14kt 18 %, 18kt 15 %, 22kt/24kt 12 %
const getMakingPct = (item, isDiamond) => {
  if (isBracelet(item)) return 0.17;
  if (isDiamond) return 0.185;
  switch ((item?.karat || "22kt").toLowerCase()) {
    case "14kt": return 0.18;
    case "18kt": return 0.15;
    case "22kt":
    case "24kt":
    default: return 0.12;
  }
};

const round0 = (n) => Math.round(n);

export const fmtINR = (n) => {
  if (n == null || isNaN(n)) return "₹0";
  return "₹" + Math.round(n).toLocaleString("en-IN");
};
